/*
 * Copyright (c) 2020 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Injectable, Injector } from '@angular/core';
import { ViewService } from '@angular-kit/view';
import { TimeClockEmployeeViewData, TimeClockEmployeeViewModel } from './time-clock-employee.view-model';
import { TimeClockEmployeeComponentModel } from '../component/time-clock-employee/time-clock-employee.component-model';
import { ChangedEvent, CoreActionKey, CoreFormatKey, hasValue } from '@typescript-kit/core';
import { TimeClockShellViewService } from './time-clock-shell.view-service';
import { ActivatedRoute } from '@angular/router';
// import { TagInfo } from '@teamworks/global';
import {
  ActionModel,
  Alignments,
  ContainerModel,
  Orientations,
  TableCellModel,
  TableColumnModel,
  TableModel, TableRowModel,
  TextModel,
  ValueModel
} from '@typescript-kit/view';
import { TimeRecordTypes } from '@teamworks/global';
import { TimeClockViewKey } from '../key/time-clock-view.key';
import { TimeClockService } from '../service/time-clock.service';

@Injectable()
export class TimeClockEmployeeViewService extends ViewService<TimeClockEmployeeViewModel, TimeClockEmployeeComponentModel> {
  private static readonly BACK_TIMEOUT = 10000;
  private readonly timeClockService: TimeClockService;
  private readonly timeClockShellViewService: TimeClockShellViewService;
  private backTimeoutHandle: any;

  constructor(
    injector: Injector,
    private readonly route: ActivatedRoute
  ) {
    super(injector);
    this.timeClockService = injector.get(TimeClockService);
    this.timeClockShellViewService = injector.get(TimeClockShellViewService);
  }

  get timeClockId(): string {
    return this.timeClockShellViewService.viewModel.timeClockId;
  }

  initialize(data?: TimeClockEmployeeViewData): void {
    const viewModel = (data && data instanceof TimeClockEmployeeViewModel) ? data : new TimeClockEmployeeViewModel(data);
    // TODO: TODO MJ: FIX OR DELETE (NfcKeyInfo -> TagInfo)
    // this.route.data.subscribe((routeData: { tagInfo: TagInfo }) => {
    //   viewModel.setValues({
    //     company: routeData.tagInfo.company,
    //     employee: routeData.tagInfo.employee,
    //     timeRecordList: routeData.tagInfo.timeRecordList
    //   });
    //   this.scheduleBackTimeout();
    // });
    super.initialize(viewModel);
  }

  finalize(): void {
    this.clearBackTimeout();
    super.finalize();
  }

  protected onViewModelPropertyChanged(event: ChangedEvent): void {
    if (event.changes.company) {
      this.refreshCompanyNameModel(this.componentModel.companyName);
    }
    if (event.changes.employee) {
      this.refreshEmployeeNameModel(this.componentModel.employeeName);
    }
    if (event.changes.timeRecordList) {
      this.refreshTimeRecordTableModel(this.componentModel.timeRecordTable);
      this.refreshActionMenuModel(this.componentModel.actionMenu);
    }
  }

  private async start() {
    return this.timeClockService.start(this.timeClockId, this.viewModel.company.id, this.viewModel.employee.id)
      .then((timeRecordList) => {
        if (this.isFinalized) {
          return;
        }
        this.viewModel.timeRecordList = timeRecordList;
        this.scheduleBackTimeout();
      })
      .catch((error) => this.alertService.alertError(error));
  }

  private async stop() {
    return this.timeClockService.stop(this.timeClockId, this.viewModel.company.id, this.viewModel.employee.id)
      .then((timeRecordList) => {
        if (this.isFinalized) {
          return;
        }
        this.viewModel.timeRecordList = timeRecordList;
        this.scheduleBackTimeout();
      })
      .catch((error) => this.alertService.alertError(error));
  }

  private back(): void {
    this.timeClockShellViewService.viewModel.nfcKey = null;
  }

  private isRecording(): boolean {
    if (!this.viewModel.timeRecordList || this.viewModel.timeRecordList.length === 0) {
      return false;
    }
    const lastTimeRecord = this.viewModel.timeRecordList[this.viewModel.timeRecordList.length - 1];
    return lastTimeRecord.type === TimeRecordTypes.WORK
      && !hasValue(lastTimeRecord.hours)
      && hasValue(lastTimeRecord.from)
      && !hasValue(lastTimeRecord.to);
  }

  private scheduleBackTimeout() {
    this.clearBackTimeout();
    this.backTimeoutHandle = setTimeout(() => {
      this.backTimeoutHandle = null;
      this.back();
    }, TimeClockEmployeeViewService.BACK_TIMEOUT);
  }

  private clearBackTimeout() {
    if (!this.backTimeoutHandle) {
      return;
    }
    clearTimeout(this.backTimeoutHandle);
    this.backTimeoutHandle = null;
  }

  protected createComponentModel(): TimeClockEmployeeComponentModel {
    return new TimeClockEmployeeComponentModel({
      items: [
        this.createEmployeeNameModel(),
        this.createCompanyNameModel(),
        this.createTimeRecordContainerModel(),
        this.createActionMenuModel()
      ]
    });
  }

  protected refreshComponentModel(componentModel: TimeClockEmployeeComponentModel): void {
    super.refreshComponentModel(componentModel);
  }

  private createEmployeeNameModel(): TextModel {
    const model = new TextModel({
      key: 'employeeName', tags: ['app-employee-name'],
    });
    this.refreshEmployeeNameModel(model);
    return model;
  }

  private refreshEmployeeNameModel(model: TextModel) {
    model.text = this.viewModel.employee ? this.viewModel.employee.name : '-';
  }

  private createCompanyNameModel(): TextModel {
    const model = new TextModel({
      key: 'companyName', tags: ['app-company-name']
    });
    this.refreshCompanyNameModel(model);
    return model;
  }

  private refreshCompanyNameModel(model: TextModel) {
    model.text = this.viewModel.company ? this.viewModel.company.name : '-';
  }

  private createTimeRecordContainerModel(): ContainerModel {
    return new ContainerModel({
      key: 'timeRecordContainer', tags: ['app-time-record-container'], orientation: Orientations.SIDE_BY_SIDE,
      items: [
        this.createTimeRecordLabelModel(),
        this.createTimeRecordTableModel(),
        new TextModel({ key: 'timeRecordSpacer', tags: ['app-time-record-label'], text: '' })
      ]
    });
  }

  private createTimeRecordLabelModel(): TextModel {
    return new TextModel({
      key: 'timeRecordLabel', tags: ['app-time-record-label'],
      text: `#(${TimeClockViewKey.EMPLOYEE}/today):`
    });
  }

  private createTimeRecordTableModel(): TableModel {
    return new TableModel({
      key: 'timeRecordTable', tags: ['app-time-record-table'],
      columnHeaderCount: 1,
      rows: [
        new TableRowModel({ index: 0, tags: ['app-header'] })
      ],
      columns: [
        new TableColumnModel({ index: 0, tags: ['app-from'] }),
        new TableColumnModel({ index: 1, tags: ['app-to'] }),
        new TableColumnModel({ index: 2, tags: ['app-hours'] })
      ],
      cells: this.createTimeRecordTableCellModels()
    });
  }

  private refreshTimeRecordTableModel(model: TableModel) {
    model.setValues({
      cells: this.createTimeRecordTableCellModels()
    });
  }

  private createTimeRecordTableCellModels(): TableCellModel[] {
    const timeRecordTableCellModels: TableCellModel[] = [
      new TableCellModel({ row: 0, column: 0, content: `${TimeClockViewKey.EMPLOYEE}/from` }),
      new TableCellModel({ row: 0, column: 1, content: `${TimeClockViewKey.EMPLOYEE}/to` }),
      new TableCellModel({ row: 0, column: 2, content: `${TimeClockViewKey.EMPLOYEE}/hours` }),
    ];
    if (!this.viewModel.timeRecordList) {
      return timeRecordTableCellModels;
    }
    this.viewModel.timeRecordList
    .filter((timeRecord) => timeRecord.type && timeRecord.type === TimeRecordTypes.WORK)
    .forEach((timeRecord, index) => {
      timeRecordTableCellModels.push(
        new TableCellModel({
          row: index + 1, column: 0, content: new ValueModel({
            formattedValue: timeRecord.from, format: CoreFormatKey.TIME, alignment: Alignments.LEFT
          })
        }),
        new TableCellModel({
          row: index + 1, column: 1, content: new ValueModel({
            formattedValue: timeRecord.to, format: CoreFormatKey.TIME, alignment: Alignments.LEFT
          })
        }),
        new TableCellModel({
          row: index + 1, column: 2, content: new ValueModel({
            value: timeRecord.hours,
            format: 'input-number-2',
            suffix: typeof timeRecord.hours === 'number' ? ' h' : null,
            alignment: Alignments.RIGHT
          })
        }),
      );
    });
    return timeRecordTableCellModels;
  }

  private createActionMenuModel(): ContainerModel {
    return new ContainerModel({
      key: 'actionMenu', tags: ['app-action-menu'], orientation: Orientations.SIDE_BY_SIDE,
      items: [
        this.createStartActionModel(),
        this.createStopActionModel(),
        this.createBackActionModel(),
      ]
    });
  }

  private refreshActionMenuModel(model: ContainerModel) {
    this.refreshStartActionModel(model.items.get('startAction') as ActionModel);
    this.refreshStopActionModel(model.items.get('stopAction') as ActionModel);
  }

  private createStartActionModel(): ActionModel {
    const model = new ActionModel({
      key: 'startAction', tags: ['app-start-action', 'btn-primary'],
      content: `${TimeClockViewKey.EMPLOYEE}/start`,
      onClick: () => {
        model.isDisabled = true;
        return this.start();
      }
    });
    this.refreshStartActionModel(model);
    return model;
  }

  private refreshStartActionModel(model: ActionModel) {
    model.isDisabled = this.isRecording();
  }

  private createStopActionModel(): ActionModel {
    const model = new ActionModel({
      key: 'stopAction', tags: ['app-stop-action', 'btn-primary'],
      content: `${TimeClockViewKey.EMPLOYEE}/stop`,
      onClick: () => {
        model.isDisabled = true;
        return this.stop();
      }
    });
    this.refreshStopActionModel(model);
    return model;
  }

  private refreshStopActionModel(model: ActionModel) {
    model.isDisabled = !this.isRecording();
  }

  private createBackActionModel(): ActionModel {
    return new ActionModel({
      key: 'backAction', tags: ['app-back-action', 'btn-primary'],
      content: CoreActionKey.BACK,
      onClick: () => this.back()
    });
  }

}
